import { useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { adminCreateModelMutation, adminUpdateModelMutation, errorMessage } from "@/api";
import { useAppForm } from "@/components/form/form";
import { AdvancedFields } from "./AdvancedFields";
import { EndpointFields } from "./EndpointFields";
import { ModelBasicsFields } from "./ModelBasicsFields";
import { emptyModelValues, type ModelFormValues, modelFormSchema, suggestionToValues, toCreateBody } from "./modelForm";
import { SuggestBar } from "./SuggestBar";
import { useRefreshModels } from "./useModelData";

/** The Add model form: optional AI quick setup, then the full configuration. */
export function NewModelForm() {
  const { t } = useTranslation("adminModels");
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshModels();
  const create = useMutation(adminCreateModelMutation());
  const update = useMutation(adminUpdateModelMutation());

  const form = useAppForm({
    defaultValues: emptyModelValues(),
    validators: { onChange: modelFormSchema },
    onSubmit: async ({ value }) => {
      try {
        const { data } = await create.mutateAsync({ body: toCreateBody(value), throwOnError: true });
        // The create schema has no API key; store a custom endpoint's key right after.
        if (value.hosting === "custom" && value.apiKeyValue) {
          await update.mutateAsync({
            path: { model_id: data.id },
            body: { apiKeyValue: value.apiKeyValue },
            throwOnError: true,
          });
        }
        await refresh();
        toast.success(t("form.created", { name: data.name }));
        navigate("/admin/models");
      } catch (error) {
        toast.error(errorMessage(error, t("form.createFailed")));
      }
    },
  });

  const prefill = (values: ModelFormValues) => {
    for (const key of Object.keys(values) as Array<keyof ModelFormValues>) {
      form.setFieldValue(key, values[key] as never);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <SuggestBar onSuggested={(s) => prefill(suggestionToValues(s))} />
      <form
        aria-label={t("form.newLabel")}
        className="flex flex-col gap-8"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <ModelBasicsFields form={form} isNew />
        <EndpointFields form={form} hasApiKey={false} />
        <AdvancedFields form={form} />
        <form.AppForm>
          <form.SubmitActions
            onCancel={() => navigate("/admin/models")}
            submitLabel={t("form.createSubmit")}
            requireChanges={false}
          />
        </form.AppForm>
      </form>
    </div>
  );
}
